import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../api';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import DiffViewer from '../components/DiffViewer';
import { confirmDialog } from '../components/ConfirmDialog';
import AuthContext from '../contexts/AuthContext';

const containerStyle = {
  padding: '1.5rem',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  backgroundColor: '#f8f9fa',
  minHeight: '100vh'
};

const chartContainer = {
  width: '100%',
  maxWidth: '800px',
  marginBottom: '2rem',
  backgroundColor: 'white',
  padding: '2rem',
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
  border: '1px solid #e9ecef'
};

const tableStyle = {
  width: '100%',
  maxWidth: '800px',
  borderCollapse: 'collapse',
  marginTop: '1rem',
  backgroundColor: 'white',
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
  border: '1px solid #e9ecef'
};

const thTdStyle = {
  border: '1px solid #ddd',
  padding: '1rem',
  textAlign: 'center',
  backgroundColor: '#f8f9fa',
  color: '#333'
};

const tdStyle = {
  ...thTdStyle,
  backgroundColor: 'white'
};

const compareContainer = {
  width: '100%',
  maxWidth: '800px',
  background: 'white',
  border: '1px solid #e9ecef',
  padding: '2rem',
  marginTop: '1rem',
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
};

const paginationStyle = {
  display: 'flex',
  justifyContent: 'center',
  padding: '2rem',
  gap: '0.5rem',
  marginTop: '1rem'
};

const buttonStyle = {
  padding: '0.5rem 1rem',
  borderRadius: '6px',
  border: '1px solid #ddd',
  backgroundColor: '#f8f9fa',
  cursor: 'pointer',
  fontWeight: '500',
  transition: 'all 0.2s ease'
};

const activeButtonStyle = {
  ...buttonStyle,
  backgroundColor: '#667eea',
  color: 'white',
  borderColor: '#667eea'
};

const disabledButtonStyle = {
  ...buttonStyle,
  opacity: '0.6',
  cursor: 'not-allowed'
};

const deleteButtonStyle = {
  padding: '0.5rem 1rem',
  borderRadius: '6px',
  backgroundColor: '#dc3545',
  color: 'white',
  border: '1px solid #dc3545',
  fontSize: '0.875rem',
  fontWeight: '500',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  marginLeft: '0.5rem'
};

const deleteButtonHoverStyle = {
  backgroundColor: '#c82333',
  borderColor: '#bd2130'
};

const adminDeleteButtonStyle = {
  ...deleteButtonStyle,
  backgroundColor: '#6f42c1',
  borderColor: '#6f42c1'
};

const adminDeleteButtonHoverStyle = {
  backgroundColor: '#5a2d91',
  borderColor: '#5a2d91'
};

const loadingStyle = {
  textAlign: 'center',
  padding: '3rem',
  backgroundColor: '#f8f9fa'
};

const spinnerStyle = {
  border: '4px solid #f3f3f3',
  borderTop: '4px solid #667eea',
  borderRadius: '50%',
  width: '50px',
  height: '50px',
  animation: 'spin 1s linear infinite',
  margin: '0 auto'
};

const skeletonCardStyle = {
  width: '100%',
  maxWidth: '800px',
  height: '2rem',
  backgroundColor: '#e0e0e0',
  borderRadius: '8px',
  marginBottom: '1rem',
  animation: 'pulse 1.5s infinite'
};

// Add keyframes for animations
const style = document.createElement('style');
style.innerHTML = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  @keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.5; }
    100% { opacity: 1; }
  }
`;
document.head.appendChild(style);

const TestResults = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [expectedText, setExpectedText] = useState('');
  const [submissions, setSubmissions] = useState([]);
  const [compareIdx, setCompareIdx] = useState(null);
  const [showExpectedModal, setShowExpectedModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userRank, setUserRank] = useState(null);
  const [totalUsers, setTotalUsers] = useState(0);
  const [leaderboard, setLeaderboard] = useState({ visible: false, entries: [] });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [userRole, setUserRole] = useState('user'); // 'user' or 'admin'
  const totalAttemptsPerPage = 5;

  useEffect(() => {
    // Disable text selection and copying on the entire page
    const container = document.body;
    container.style.userSelect = 'none';
    container.style.webkitUserSelect = 'none';
    container.style.mozUserSelect = 'none';
    container.style.msUserSelect = 'none';

    const preventCopy = (e) => {
      e.preventDefault();
    };

    const preventContextMenu = (e) => {
      e.preventDefault();
    };

    const preventKeyCopy = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'C' || e.key === 'v' || e.key === 'V' || e.key === 'x' || e.key === 'X' || e.key === 'a' || e.key === 'A')) {
        e.preventDefault();
      }
    };

    document.addEventListener('copy', preventCopy);
    document.addEventListener('cut', preventCopy);
    document.addEventListener('paste', preventCopy);
    document.addEventListener('contextmenu', preventContextMenu);
    document.addEventListener('keydown', preventKeyCopy);

    return () => {
      // Re-enable on unmount
      container.style.userSelect = '';
      container.style.webkitUserSelect = '';
      container.style.mozUserSelect = '';
      container.style.msUserSelect = '';

      document.removeEventListener('copy', preventCopy);
      document.removeEventListener('cut', preventCopy);
      document.removeEventListener('paste', preventCopy);
      document.removeEventListener('contextmenu', preventContextMenu);
      document.removeEventListener('keydown', preventKeyCopy);
    };
  }, []);

  useEffect(() => {
    // Inject Kruti Dev font for modal
    const styleId = 'kruti-modal-font';
    let style = document.getElementById(styleId);
    if (!style) {
      style = document.createElement('style');
      style.id = styleId;
      style.innerHTML = `
        @font-face {
          font-family: 'KrutiDev010';
          src: url('/fonts/KrutiDev_010.ttf') format('truetype');
          font-weight: normal;
          font-style: normal;
        }
        .kruti-modal-text {
          font-family: 'KrutiDev010', monospace !important;
          font-size: 18px;
          line-height: 1.6;
        }
      `;
      document.head.appendChild(style);
    }

    return () => {
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        document.head.removeChild(existingStyle);
      }
    };
  }, []);

  useEffect(() => {
    const fetchResults = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await api.get(`/tests/${id}/results`, {
          params: {
            page: currentPage,
            limit: totalAttemptsPerPage
          }
        });
        setExpectedText(data.expectedText);
        setSubmissions(data.submissions);
        setUserRank(data.userRank);
        setTotalUsers(data.totalUsers);
        setLeaderboard(data.leaderboard || { visible: false, entries: [] });
        setTotalPages(data.pagination ? data.pagination.totalPages : 1);
      } catch (err) {
        console.error(err);
        setError('Failed to fetch test results');
      } finally {
        setLoading(false);
      }
    };
    fetchResults();
  }, [id, currentPage]);

  // Check user role on component mount
  useEffect(() => {
    const checkUserRole = async () => {
      try {
        const response = await api.get('/auth/me');
        setUserRole(response.data.user.isAdmin ? 'admin' : 'user');
      } catch (error) {
        console.error('Failed to fetch user role:', error);
        setUserRole('user');
      }
    };
    checkUserRole();
  }, []);

  const handleCompare = (idx) => {
    setCompareIdx(idx);
  };

  const closeCompare = () => {
    setCompareIdx(null);
  };

  const handleDeleteAttempt = async (submissionId) => {
    try {
      const confirmed = await confirmDialog(
        'Delete this attempt?',
        'This will delete only this specific attempt result. This action cannot be undone.'
      );
      
      if (!confirmed) return;

      setLoading(true);
      console.log('Attempting to delete submission:', submissionId, 'for test:', id);
      
      const response = await api.delete(`/tests/${id}/submissions/${submissionId}`);
      console.log('Delete response:', response.data);
      
      // Show success message
      alert('Successfully deleted the attempt.');
      
      // Refresh the results
      fetchResults();
    } catch (error) {
      console.error('Error deleting attempt:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url
      });
      
      let errorMessage = 'Failed to delete the attempt. Please try again.';
      if (error.response?.status === 404) {
        errorMessage = 'Attempt not found. It may have already been deleted.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Access denied. You can only delete your own attempts.';
      }
      
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTest = async () => {
    try {
      const confirmed = await confirmDialog(
        'Delete this test and all its attempts?',
        'This will delete the entire test and all associated attempts. This action cannot be undone.'
      );
      
      if (!confirmed) return;

      setLoading(true);
      await api.delete(`/tests/${id}`);
      
      // Show success message
      alert('Successfully deleted the test and all its attempts.');
      
      // Navigate back to tests overview
      navigate('/tests');
    } catch (error) {
      console.error('Error deleting test:', error);
      alert('Failed to delete the test. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchResults = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/tests/${id}/results`, {
        params: {
          page: currentPage,
          limit: totalAttemptsPerPage
        }
      });
      setExpectedText(data.expectedText);
      setSubmissions(data.submissions);
      setUserRank(data.userRank);
      setTotalUsers(data.totalUsers);
      setLeaderboard(data.leaderboard || { visible: false, entries: [] });
      setTotalPages(data.pagination ? data.pagination.totalPages : 1);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch test results');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pageNumbers = [];
    const delta = 2;
    
    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 || 
        i === totalPages || 
        (i >= currentPage - delta && i <= currentPage + delta)
      ) {
        pageNumbers.push(i);
      } else if (
        i === currentPage - delta - 1 || 
        i === currentPage + delta + 1
      ) {
        pageNumbers.push('...');
      }
    }

    return (
      <div style={paginationStyle}>
        <button 
          style={currentPage === 1 || loading ? disabledButtonStyle : buttonStyle}
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1 || loading}
        >
          Previous
        </button>
        
        {pageNumbers.map((number, index) => (
          number === '...' ? (
            <span key={`ellipsis-${index}`} style={{ padding: '0 0.5rem', color: '#6c757d' }}>...</span>
          ) : (
            <button
              key={number}
              style={number === currentPage ? activeButtonStyle : buttonStyle}
              onClick={() => handlePageChange(number)}
              disabled={loading}
            >
              {number}
            </button>
          )
        ))}
        
        <button 
          style={currentPage === totalPages || loading ? disabledButtonStyle : buttonStyle}
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages || loading}
        >
          Next
        </button>
      </div>
    );
  };

  // Render skeleton loaders when loading
  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
          color: 'white', 
          padding: '2rem', 
          borderRadius: '16px', 
          textAlign: 'center', 
          marginBottom: '2rem'
        }}>
          <h2 style={{ margin: 0 }}>Test Results</h2>
        </div>
        <div style={chartContainer}>
          <div style={skeletonCardStyle}></div>
          <div style={{...skeletonCardStyle, height: '300px'}}></div>
        </div>
        
        <div style={{...chartContainer, marginTop: '2rem'}}>
          <div style={skeletonCardStyle}></div>
          <div style={{...skeletonCardStyle, height: '200px'}}></div>
        </div>
      </div>
    );
  }

  if (error) {
    return <div style={loadingStyle}>Error: {error}</div>;
  }

  return (
    <div style={{ ...containerStyle, userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none' }}>
      <div style={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
        color: 'white', 
        padding: '2rem', 
        borderRadius: '16px', 
        textAlign: 'center', 
        marginBottom: '2rem',
        boxShadow: '0 10px 40px rgba(102, 126, 234, 0.3)',
        position: 'relative'
      }}>
        <h2 style={{ margin: 0, fontSize: '2rem' }}>Test Results</h2>
        {/* <button
          onClick={handleDeleteTest}
          style={userRole === 'admin' ? adminDeleteButtonStyle : deleteButtonStyle}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = userRole === 'admin' ? adminDeleteButtonHoverStyle.backgroundColor : deleteButtonHoverStyle.backgroundColor;
            e.target.style.borderColor = userRole === 'admin' ? adminDeleteButtonHoverStyle.borderColor : deleteButtonHoverStyle.borderColor;
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = userRole === 'admin' ? adminDeleteButtonStyle.backgroundColor : deleteButtonStyle.backgroundColor;
            e.target.style.borderColor = userRole === 'admin' ? adminDeleteButtonStyle.borderColor : deleteButtonStyle.borderColor;
          }}
          disabled={loading}
        >
          {userRole === 'admin' ? 'Delete Test' : 'Delete My Results'}
        </button> */}
        {submissions.length > 0 && userRank && (
          <div style={{ marginTop: '1rem', fontSize: '1.1rem', backgroundColor: 'rgba(255,255,255,0.2)', padding: '0.5rem 1rem', borderRadius: '20px', display: 'inline-block' }}>
            Your Global Rank: # {userRank} out of {totalUsers}
          </div>
        )}
        {submissions.length > 0 && (
          <div style={{ marginTop: '1rem', fontSize: '1.1rem' }}>
            {submissions.length} attempts • Avg Accuracy: {Math.round(submissions.reduce((sum, s) => sum + s.accuracy, 0) / submissions.length)}% • Avg WPM: {Math.round(submissions.reduce((sum, s) => sum + s.wpm, 0) / submissions.length)}
          </div>
        )}
        {submissions.length > 0 && (
          <button
            onClick={() => setShowExpectedModal(true)}
            style={{
              marginTop: '1rem',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              fontSize: '1rem'
            }}
            onMouseEnter={e => {
              e.target.style.backgroundColor = '#218838';
              e.target.style.transform = 'scale(1.02)';
            }}
            onMouseLeave={e => {
              e.target.style.backgroundColor = '#28a745';
              e.target.style.transform = 'scale(1)';
            }}
          >
            View PDF
          </button>
        )}
      </div>
      {loading && (
        <div style={loadingStyle}>
          <div style={spinnerStyle}></div>
        </div>
      )}

      {submissions.length === 0 && !loading ? (
        <div style={loadingStyle}>No submissions found for this test.</div>
      ) : (
        <>

          {compareIdx !== null && (
            <div style={compareContainer}>
              <h3 style={{ color: '#2c3e50', marginBottom: '1rem' }}>Original vs Typed Comparison</h3>
              <div className="kruti-text" style={{ borderRadius: '8px', overflow: 'hidden' }}>
                <DiffViewer
                  original={expectedText}
                  typed={submissions[compareIdx].typedText}
                />
              </div>

              <button 
                onClick={closeCompare} 
                style={{ 
                  marginTop: '1rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={e => {
                  e.target.style.backgroundColor = '#5a6268';
                  e.target.style.boxShadow = '0 4px 12px rgba(108, 117, 125, 0.3)';
                }}
                onMouseLeave={e => {
                  e.target.style.backgroundColor = '#6c757d';
                  e.target.style.boxShadow = 'none';
                }}
              >
                Close Comparison
              </button>
            </div>
          )}

          <div style={{ 
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '1.5rem',
            marginBottom: '1rem',
            boxShadow: '0 4px 16px rgba(0,0,0,0.05)',
            border: '1px solid #e9ecef'
          }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thTdStyle}>Attempt Time</th>
                  <th style={thTdStyle}>Total Words</th>
                  <th style={thTdStyle}>Correct Words</th>
                  <th style={thTdStyle}>Wrong Words</th>
                  <th style={thTdStyle}>Marks</th>
                  <th style={thTdStyle}>Compare</th>
                  <th style={thTdStyle}>Delete</th>
                </tr>
              </thead>
              <tbody>
                {submissions.map((s, idx) => (
                  <tr key={idx} style={{ transition: 'background-color 0.3s ease' }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                  >
                    <td style={tdStyle}>{new Date(s.createdAt).toLocaleString()}</td>
                    <td style={tdStyle}>{s.totalWords || 0}</td>
                    <td style={tdStyle}>{s.correctWords || 0}</td>
                    <td style={tdStyle}>{s.wrongWords || 0}</td>
                    <td style={{
                      ...tdStyle,
                      color: s.accuracy > 80 ? '#28a745' : s.accuracy > 60 ? '#f39c12' : '#e74c3c',
                      fontWeight: 'bold'
                    }}>
                      <span style={{ padding: '0.25rem 0.5rem', borderRadius: '12px', backgroundColor: s.accuracy > 80 ? 'rgba(40, 167, 69, 0.1)' : s.accuracy > 60 ? 'rgba(243, 156, 18, 0.1)' : 'rgba(231, 76, 60, 0.1)' }}>
                        {s.accuracy || 0}%
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <button 
                        onClick={() => handleCompare(idx)}
                        style={{
                          backgroundColor: '#667eea',
                          color: 'white',
                          border: 'none',
                          padding: '0.5rem 1rem',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={e => {
                          e.target.style.backgroundColor = '#5a67d8';
                          e.target.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={e => {
                          e.target.style.backgroundColor = '#667eea';
                          e.target.style.transform = 'scale(1)';
                        }}
                      >
                        Compare
                      </button>
                    </td>
                    <td style={tdStyle}>
                      <button 
                        onClick={() => {
                          console.log('Submission data:', s);
                          handleDeleteAttempt(s.id);
                        }}
                        style={{
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={e => {
                          e.target.style.backgroundColor = '#c82333';
                          e.target.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={e => {
                          e.target.style.backgroundColor = '#dc3545';
                          e.target.style.transform = 'scale(1)';
                        }}
                        disabled={loading}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {renderPagination()}
        </>
      )}
      {showExpectedModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowExpectedModal(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '2rem',
              borderRadius: '16px',
              maxWidth: '80%',
              maxHeight: '80%',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              position: 'relative',
              minWidth: '400px'
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, color: '#2c3e50', textAlign: 'center', marginBottom: '1rem' }}>
              PDF
            </h3>
            <div
              className="kruti-text kruti-modal-text"
              style={{
                border: '1px solid #e9ecef',
                borderRadius: '8px',
                padding: '1rem',
                minHeight: '200px',
                maxHeight: '400px',
                whiteSpace: 'pre-wrap',
                backgroundColor: '#f8f9fa',
                marginBottom: '1rem',
                overflow: 'auto'
              }}
              dangerouslySetInnerHTML={{ __html: expectedText }}
            />
            <div style={{ textAlign: 'center' }}>
              <button
                onClick={() => setShowExpectedModal(false)}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  transition: 'all 0.3s ease',
                  fontSize: '1rem'
                }}
                onMouseEnter={e => {
                  e.target.style.backgroundColor = '#5a6268';
                  e.target.style.transform = 'scale(1.02)';
                }}
                onMouseLeave={e => {
                  e.target.style.backgroundColor = '#6c757d';
                  e.target.style.transform = 'scale(1)';
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {leaderboard.visible && (
        <div style={{
          width: '100%',
          maxWidth: '800px',
          backgroundColor: 'white',
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
          border: '1px solid #e9ecef',
          overflow: 'hidden',
          marginBottom: '1.5rem'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0.85rem 1rem',
            background: 'linear-gradient(135deg, #ffd76a, #f39c12)',
            color: '#1f2937'
          }}>
            <h3 style={{ margin: 0, fontSize: '1.4rem' }}>Ranklist for test </h3>
          </div>

          <div style={{ padding: '0.75rem' }}>
            {leaderboard.entries.map((entry, index) => {
              const rankColors = {
                1: { background: 'linear-gradient(135deg, #f6d365, #fda085)', color: '#5a3d00' },
                2: { background: 'linear-gradient(135deg, #dfe6e9, #b2bec3)', color: '#2d3436' },
                3: { background: 'linear-gradient(135deg, #f7c59f, #d88c5a)', color: '#4a2d20' },
                default: { background: '#f8f9fa', color: '#2c3e50' }
              };
              const isCurrentUser = Number(entry.userId) === Number(user?.id);
              const medal = entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : entry.rank === 3 ? '🥉' : `#${entry.rank}`;
              const colors = rankColors[entry.rank] || rankColors.default;

              return (
                <div
                  key={`${entry.userId || 'user'}-${entry.rank || index}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0.6rem 0.75rem',
                    marginBottom: index === leaderboard.entries.length - 1 ? 0 : '0.45rem',
                    borderRadius: '10px',
                    border: '1px solid #edf2f7',
                    boxShadow: isCurrentUser ? '0 0 0 2px rgba(102,126,234,0.5), 0 4px 12px rgba(102,126,234,0.18)' : 'none',
                    ...colors
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                    <div style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '0.75rem',
                      background: 'rgba(255,255,255,0.35)'
                    }}>
                      {medal}
                    </div>
                    <span style={{ fontWeight: '700' }}>{entry.name}</span>
                  </div>
                  <span style={{ fontWeight: '700' }}>{entry.accuracy !== null ? `${Number(entry.accuracy).toFixed(2)}%` : ''}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      <Link to="/tests" style={{ 
        marginTop: '2rem', 
        textDecoration: 'none', 
        color: '#667eea',
        fontWeight: 'bold',
        fontSize: '16px',
        padding: '0.75rem 1.5rem',
        border: '2px solid #667eea',
        borderRadius: '8px',
        transition: 'all 0.3s ease',
        display: 'inline-block'
      }}
      onMouseEnter={e => {
        e.target.style.backgroundColor = '#667eea';
        e.target.style.color = 'white';
      }}
      onMouseLeave={e => {
        e.target.style.backgroundColor = 'transparent';
        e.target.style.color = '#667eea';
      }}
      >
        ← Back to Tests
      </Link>
    </div>
  );
};

export default TestResults;
